import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

const KLAVIYO_REVISION = '2026-07-15';
const KLAVIYO_CLIENT_URL = 'https://a.klaviyo.com/client';

/**
 * Back-in-stock form that talks to Klaviyo's client API directly.
 * Copy is rendered from theme locale files; the storefront locale is written
 * to the Klaviyo profile so the flow can send the email in the right language.
 *
 * @typedef {Object} Refs
 * @property {HTMLFormElement} form
 * @property {HTMLInputElement} email
 * @property {HTMLButtonElement} submit
 * @property {HTMLElement} message
 * @property {HTMLElement} messageText
 * @property {HTMLInputElement} [consent]
 *
 * @extends {Component<Refs>}
 */
class BackInStock extends Component {
  requiredRefs = ['form', 'email', 'submit', 'message', 'messageText'];

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();

    // Same scoping as Horizon's product-inventory component
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /**
   * Shows or hides the form depending on the selected variant's availability.
   * @param {ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    // Ignore swatch changes inside product cards on the same section
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail) return;

        const { newProduct, resource } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        // Keep the form visible in the theme editor as a preview
        const isDesignMode = 'designMode' in this.dataset;

        // resource === null means the option combination does not exist
        if (!resource) {
          this.hidden = !isDesignMode;
          return;
        }

        this.dataset.variantId = String(resource.id);
        this.hidden = !isDesignMode && resource.available !== false;
        this.#resetMessage();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[back-in-stock] Event promise rejected:', error);
      });
  };

  /**
   * @param {SubmitEvent} event
   */
  handleSubmit = async (event) => {
    event.preventDefault();

    const { email, submit, consent } = this.refs;
    const address = email.value.trim();

    if (!email.checkValidity() || !address) {
      this.#showMessage('invalid');
      email.focus();
      return;
    }

    submit.disabled = true;
    submit.setAttribute('aria-busy', 'true');

    try {
      const wantsNewsletter = Boolean(consent?.checked && this.dataset.listId);

      // Each request is evaluated on its own, so one failure does not mask the others
      const [subscriptionResult, , newsletterResult] = await Promise.all([
        this.#getResult(this.#subscribeBackInStock(address), 'Back-in-stock subscription'),
        this.#getResult(this.#updateProfileLocale(address), 'Profile locale update'),
        wantsNewsletter ? this.#getResult(this.#subscribeToList(address), 'Newsletter subscription') : 'success',
      ]);

      if (subscriptionResult !== 'success') {
        this.#showMessage(subscriptionResult);
        return;
      }

      this.refs.form.reset();
      this.#showMessage(newsletterResult === 'success' ? 'success' : 'newsletterError');
    } finally {
      submit.disabled = false;
      submit.removeAttribute('aria-busy');
    }
  };

  /**
   * @param {string} email
   */
  #subscribeBackInStock(email) {
    return this.#post('back-in-stock-subscriptions', {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: { email },
            },
          },
          channels: ['EMAIL'],
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: `$shopify:::$default:::${this.dataset.variantId}`,
            },
          },
        },
      },
    });
  }

  /**
   * Stores the storefront language on the profile. Used for conditional
   * splits or Smart Translations in the back-in-stock flow.
   * @param {string} email
   */
  #updateProfileLocale(email) {
    return this.#post('profiles', {
      data: {
        type: 'profile',
        attributes: {
          email,
          locale: this.dataset.locale,
          properties: {
            shop_language: this.dataset.language,
          },
        },
      },
    });
  }

  /**
   * Optional newsletter opt-in (only when the checkbox is ticked).
   * @param {string} email
   */
  #subscribeToList(email) {
    return this.#post('subscriptions', {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Back in Stock form',
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email,
                subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
              },
            },
          },
        },
        relationships: {
          list: { data: { type: 'list', id: this.dataset.listId } },
        },
      },
    });
  }

  /**
   * @param {string} endpoint
   * @param {object} payload
   */
  #post(endpoint, payload) {
    const url = `${KLAVIYO_CLIENT_URL}/${endpoint}/?company_id=${encodeURIComponent(this.dataset.companyId ?? '')}`;

    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        accept: 'application/vnd.api+json',
        revision: KLAVIYO_REVISION,
      },
      body: JSON.stringify(payload),
    });
  }

  /**
   * Maps a request to 'success', 'error' (Klaviyo rejected it) or 'blocked' (no response) and logs failures.
   * @param {Promise<Response>} request
   * @param {string} label
   * @returns {Promise<'success' | 'error' | 'blocked'>}
   */
  async #getResult(request, label) {
    try {
      // 202 = accepted; duplicates for the same variant are silently ignored by Klaviyo
      const response = await request;
      if (response.ok) return 'success';

      const body = await response.json().catch(() => null);
      console.warn(`[back-in-stock] ${label} failed with ${response.status}:`, body?.errors ?? body);
      return 'error';
    } catch (error) {
      // fetch rejects without a response when tracking protection or ad blockers block the domain
      console.warn(`[back-in-stock] ${label} was blocked or could not be sent:`, error);
      return 'blocked';
    }
  }

  /**
   * @param {'success' | 'error' | 'blocked' | 'invalid' | 'newsletterError'} state
   */
  #showMessage(state) {
    const { message, messageText } = this.refs;
    message.dataset.state = state;
    messageText.textContent = message.dataset[state] ?? '';
  }

  #resetMessage() {
    const { message, messageText } = this.refs;
    delete message.dataset.state;
    messageText.textContent = '';
  }
}

if (!customElements.get('back-in-stock')) {
  customElements.define('back-in-stock', BackInStock);
}
